import { useCallback, useEffect, useState } from 'react';
import useApiRequest from '../../../../lib/hooks/useRequest';
import { Button } from '../../../../ui/components/ui/button';
import Preloader from '../../../../ui/components/ui/preloader';
import { BillingTermMetaResponseDto } from '../../../products/types/response';
import { OfferConfirmAction } from '../../constants/offer-confirm-action.enum';
import { useOfferActions } from '../../hooks/use-offer-actions';
import { OfferResponseDto } from '../../types/response';
import OfferConfirmDialog from '../offer-confirm-dialog/offer-confirm-dialog';
import SendOfferDialog from '../send-offer-dialog/send-offer-dialog';
import OfferDetail from './offer-detail';

export interface OfferDetailWrapperProps {
  offerId: string;
  billingTerms: BillingTermMetaResponseDto[];
  onBack: () => void;
  onCorrect: () => void;
}

type LoadedOffer = { status: 'ready'; offer: OfferResponseDto } | { status: 'notFound' };

const OfferDetailWrapper = ({ offerId, billingTerms, onBack, onCorrect }: OfferDetailWrapperProps) => {
  const { apiRequest } = useApiRequest();
  const [loaded, setLoaded] = useState<LoadedOffer | null>(null);

  const fetchOffer = useCallback(async () => {
    const res = await apiRequest<OfferResponseDto>(`offers/admin/${offerId}`, 'GET');
    setLoaded(res.data ? { status: 'ready', offer: res.data } : { status: 'notFound' });
  }, [apiRequest, offerId]);

  useEffect(() => {
    fetchOffer();
  }, [fetchOffer]);

  const { meta, openSend, openOfferFile, openConfirm, sendDialogProps, confirmDialogProps } = useOfferActions(fetchOffer);

  if (!loaded || !meta) return <Preloader />;

  if (loaded.status === 'notFound') {
    return (
      <div className="flex flex-col items-start gap-3 rounded-lg bg-white px-9 py-7">
        <p className="text-[16px] text-[#12282A]">Dieses Angebot gibt es nicht (mehr).</p>
        <Button variant="outline" onClick={onBack}>
          Zur Übersicht
        </Button>
      </div>
    );
  }

  const target = { _id: loaded.offer._id, mail: loaded.offer.customer.mail, isSent: !!loaded.offer.sentAt };

  return (
    <>
      <OfferDetail
        offer={loaded.offer}
        billingTerms={billingTerms}
        meta={meta}
        onSend={() => openSend(target)}
        onRevoke={() => openConfirm(target, OfferConfirmAction.REVOKE)}
        onArchive={() => openConfirm(target, OfferConfirmAction.ARCHIVE)}
        onCorrect={onCorrect}
        onViewOfferFile={() => openOfferFile(loaded.offer._id)}
      />
      {sendDialogProps && <SendOfferDialog {...sendDialogProps} />}
      <OfferConfirmDialog {...confirmDialogProps} />
    </>
  );
};

export default OfferDetailWrapper;
